import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { createScenario, type HttpActionMethod } from '@/api/scenarios'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ── Schema ─────────────────────────────────────────────────────────────────

const HTTP_METHODS: HttpActionMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

const actionSchema = z.object({
  enabled:         z.boolean().default(false),
  method:          z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).default('GET'),
  path_match:      z.string().default(''),
  response_status: z.coerce.number().int().min(100).max(599).default(200),
  not_found_status:z.coerce.number().int().min(100).max(599).default(404),
})

const schema = z.object({
  scenarioName:       z.string().min(1).max(100),
  priority:           z.coerce.number().int().default(0),
  enabled:            z.boolean().default(true),
  state_key:          z.string().min(1).max(100)
                        .regex(/^[a-z0-9_-]+$/),
  id_field:           z.string().min(1).default('id'),
  id_strategy_type:   z.enum(['uuid', 'sequential', 'from_body'] as const).default('uuid'),
  id_strategy_field:  z.string().default(''),
  max_items:          z.coerce.number().int().positive().default(100),
  ttl_seconds:        z.coerce.number().int().positive().default(86400),
  actions: z.object({
    create: actionSchema,
    list:   actionSchema,
    get:    actionSchema,
    update: actionSchema,
    delete: actionSchema,
  }),
})

type FormValues = z.infer<typeof schema>
type ActionName = 'create' | 'list' | 'get' | 'update' | 'delete'

// ── Default values ─────────────────────────────────────────────────────────

const ACTION_DEFAULTS: Record<ActionName, z.infer<typeof actionSchema>> = {
  create: { enabled: true,  method: 'POST',   path_match: '/items',     response_status: 201, not_found_status: 404 },
  list:   { enabled: true,  method: 'GET',    path_match: '/items',     response_status: 200, not_found_status: 404 },
  get:    { enabled: true,  method: 'GET',    path_match: '/items/:id', response_status: 200, not_found_status: 404 },
  update: { enabled: true,  method: 'PUT',    path_match: '/items/:id', response_status: 200, not_found_status: 404 },
  delete: { enabled: true,  method: 'DELETE', path_match: '/items/:id', response_status: 204, not_found_status: 404 },
}

// ACTION_LABELS are now translated via i18n in the components that use them

// ── ActionCard ─────────────────────────────────────────────────────────────

interface ActionCardProps {
  name: ActionName
  control: ReturnType<typeof useForm<FormValues>>['control']
  register: ReturnType<typeof useForm<FormValues>>['register']
  watch: ReturnType<typeof useForm<FormValues>>['watch']
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors']
}

function ActionCard({ name, control, register, watch, errors }: ActionCardProps) {
  const enabled = watch(`actions.${name}.enabled`)
  const actionErrors = errors.actions?.[name]
  const { t } = useTranslation()
  const actionLabel = `${name.charAt(0).toUpperCase() + name.slice(1)} (${t(`endpoint.actionLabels.${name}`)})`

  return (
    <div className={cn('rounded-md border p-3 space-y-3', !enabled && 'opacity-60')}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{actionLabel}</span>
        <Controller
          name={`actions.${name}.enabled`}
          control={control}
          render={({ field }) => (
            <Switch checked={field.value} onCheckedChange={field.onChange} />
          )}
        />
      </div>

      {enabled && (
        <div className="space-y-2">
          <div className="flex gap-2 items-start">
            {/* Method */}
            <div className="w-[110px] shrink-0 space-y-1">
              <Label className="text-xs">Method</Label>
              <Controller
                name={`actions.${name}.method`}
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HTTP_METHODS.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {/* Path */}
            <div className="flex-1 space-y-1">
              <Label className="text-xs">{t('scenarioForm.pathPattern')}</Label>
              <Input
                className="h-8 font-mono text-sm"
                placeholder="/items/:id"
                {...register(`actions.${name}.path_match`)}
              />
              {actionErrors?.path_match && (
                <p className="text-xs text-destructive">{actionErrors.path_match.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t('scenarioForm.successStatus')}</Label>
              <Input className="h-8" type="number" {...register(`actions.${name}.response_status`)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('scenarioForm.notFoundStatus')}</Label>
              <Input className="h-8" type="number" {...register(`actions.${name}.not_found_status`)} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────

interface StatefulCollectionFormProps {
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

export function StatefulCollectionForm({ endpointId, onSuccess, onCancel }: StatefulCollectionFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { register, handleSubmit, control, watch, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: {
        scenarioName: '',
        priority: 0,
        enabled: true,
        state_key: 'items',
        id_field: 'id',
        id_strategy_type: 'uuid',
        id_strategy_field: '',
        max_items: 100,
        ttl_seconds: 86400,
        actions: ACTION_DEFAULTS,
      },
    })

  const idStrategyType = watch('id_strategy_type')

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      // 有効なアクションを収集してバリデーション
      const actionNames: ActionName[] = ['create', 'list', 'get', 'update', 'delete']
      const actions: Record<string, { method: HttpActionMethod; path_match: string; response_status: number; not_found_status: number }> = {}

      for (const name of actionNames) {
        const a = data.actions[name]
        if (!a.enabled) continue
        if (!a.path_match.trim()) throw new Error(t('scenarioForm.actionPathRequired', { action: name }))
        actions[name] = {
          method: a.method,
          path_match: a.path_match.trim(),
          response_status: a.response_status,
          not_found_status: a.not_found_status,
        }
      }

      if (Object.keys(actions).length === 0) {
        throw new Error(t('scenarioForm.atLeastOneAction'))
      }

      const id_strategy = data.id_strategy_type === 'from_body'
        ? `from_body:${data.id_strategy_field || 'id'}`
        : data.id_strategy_type

      await createScenario(endpointId, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        type: 'stateful_collection',
        config: {
          state_key: data.state_key,
          id_field: data.id_field,
          id_strategy,
          actions,
          max_items: data.max_items,
          ttl_seconds: data.ttl_seconds,
        },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(
        err instanceof ApiError ? err.message :
        err instanceof Error ? err.message :
        t('scenarioForm.createFailed')
      )
    },
  })

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>
      )}

      {/* ── Scenario settings ──────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="scenarioName">{t('scenarioForm.scenarioName')}</Label>
            <Input id="scenarioName" placeholder={t('scenarioForm.scenarioNamePlaceholder')} {...register('scenarioName')} />
            {errors.scenarioName && (
              <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="priority">{t('scenarioForm.priority')}</Label>
            <Input id="priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
              )}
            />
          </div>
        </div>
      </div>

      {/* ── Collection settings ──────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.collectionSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="state_key">{t('scenarioForm.stateKey')}</Label>
            <Input id="state_key" placeholder="items" className="font-mono" {...register('state_key')} />
            <p className="text-xs text-muted-foreground">{t('scenarioForm.stateKeyHint')}</p>
            {errors.state_key && (
              <p className="text-sm text-destructive">{t('scenarioForm.stateKeyRequired')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="id_field">{t('scenarioForm.idField')}</Label>
            <Input id="id_field" placeholder="id" className="font-mono" {...register('id_field')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('scenarioForm.idStrategy')}</Label>
            <Controller
              name="id_strategy_type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uuid">{t('scenarioForm.idStrategyUuid')}</SelectItem>
                    <SelectItem value="sequential">{t('scenarioForm.idStrategySequential')}</SelectItem>
                    <SelectItem value="from_body">{t('scenarioForm.idStrategyFromBody')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          {idStrategyType === 'from_body' && (
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="id_strategy_field">{t('scenarioForm.idStrategyField')}</Label>
              <Input id="id_strategy_field" placeholder="id" className="font-mono" {...register('id_strategy_field')} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="max_items">{t('scenarioForm.maxItems')}</Label>
            <Input id="max_items" type="number" min={1} {...register('max_items')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ttl_seconds">{t('scenarioForm.ttlSeconds')}</Label>
            <Input id="ttl_seconds" type="number" min={1} {...register('ttl_seconds')} />
            <p className="text-xs text-muted-foreground">{t('scenarioForm.ttlHint')}</p>
          </div>
        </div>
      </div>

      {/* ── Action settings ────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.actionSettings')}</h4>
        <p className="text-xs text-muted-foreground">
          {t('scenarioForm.actionSettingsHint')}
        </p>
        <div className="space-y-2">
          {(['create', 'list', 'get', 'update', 'delete'] as ActionName[]).map(name => (
            <ActionCard
              key={name}
              name={name}
              control={control}
              register={register}
              watch={watch}
              errors={errors}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? t('common.creating') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
